import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';

import settings from '@src/conf';
import { convertPythonTimestampToJsTimestamp, getTimezone } from '@src/corelib/utils/dateUtils';
import { parseJwt } from '@src/corelib/utils/jwtUtils';


class BaseAuthProvider {
  get isAuthenticated() {
    throw Error('The isAuthenticated getter is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  async login(username, password) {
    throw Error('The login method is not implemented.');
  }

  async logout() {
    throw Error('The logout method is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  authenticate(data) {
    throw Error('The authenticate method is not implemented.');
  }

  unauthenticate() {
    throw Error('The unauthenticate method is not implemented.');
  }
}

class BaseJwtAuthProvider extends BaseAuthProvider {
  constructor({ storage, credentials }) {
    super();
    this.storage = storage;
    this.credentials = credentials;
  }

  get config() {
    return settings.AUTH.JWT;
  }

  get isAuthenticated() {
    return !this.isAccessTokenExpired && Boolean(this.credentials);
  }

  get isAccessTokenExpired() {
    const expiredAt = this.credentials?.metadata?.accessTokenExpiredAt;

    if (isNil(expiredAt)) {
      return true;
    }

    const delta = expiredAt - Date.now();
    return delta < this.config.EXPIRED_BUFFER;
  }

  get isRefreshTokenExpired() {
    const expiredAt = this.credentials?.metadata?.refreshTokenExpiredAt;

    if (isNil(expiredAt)) {
      return true;
    }

    const delta = expiredAt - Date.now();
    return delta < this.config.EXPIRED_BUFFER;
  }

  authenticate(data) {
    const credentials = this._createCredentials(data);
    return this._setCredentials(credentials);
  }

  unauthenticate() {
    return this._removeCredentials();
  }

  _createCredentials(data) {
    const access = data[this.config.ACCESS_TOKEN_FIELD];
    const refresh = data[this.config.REFRESH_TOKEN_FIELD];
    const user = data[this.config.USER_FIELD];
    const credentials = {
      access,
      refresh,
      metadata: {
        accessTokenExpiredAt: this._parseTokenExpiredAt(access),
        refreshTokenExpiredAt: this._parseTokenExpiredAt(refresh),
      },
      user,
      loginAs: !!(data.loginAs || this.credentials?.loginAs),
    };
    return credentials;
  }

  _setCredentials(credentials) {
    this.credentials = credentials;
    this.storage.set(this.credentials);
    return this.credentials;
  }

  _removeCredentials() {
    this.credentials = null;
    this.storage.remove();
    return this.credentials;
  }

  _parseToken(token) {
    let data = {};
    try {
      data = parseJwt(token);
    }
    catch (error) {
      console.error('Error decoding token:', error);
    }
    return data;
  }

  _parseTokenExpiredAt(token) {
    const data = this._parseToken(token);
    return isFinite(data?.exp) ? convertPythonTimestampToJsTimestamp(data.exp) : null;
  }

  // eslint-disable-next-line no-unused-vars
  async createToken(username, password) {
    throw Error('The createToken method is not implemented.');
  }

  async revokeToken() {
    throw Error('The revokeToken method is not implemented.');
  }

  async verifyToken() {
    throw Error('The verifyToken method is not implemented.');
  }

  async refreshToken(loginAsToken) {
    throw Error('The refreshToken method is not implemented.');
  }
}


class JwtAuthProvider extends BaseJwtAuthProvider {
  constructor({ storage, credentials }) {
    super({ storage, credentials });
  }

  async login(username, password) {
    return await this.createToken(username, password);
  }

  async logout() {
    return await this.revokeToken();
  }

  async createToken(username, password) {
    const url = this.config.TOKEN_CREATE_URL;
    const payload = {
      [this.config.USERNAME_FIELD]: username,
      [this.config.PASSWORD_FIELD]: password,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'User-Timezone': getTimezone(),
        },
      });

      if (!response.ok) {
        this.unauthenticate();
        throw new Error(`Login Failed: ${response.status}`);
      }

      const data = await response.json();
      return this.authenticate(data);
    }
    catch (error) {
      this.unauthenticate();
      throw error;
    }
  }

  async revokeToken() {
    return this.unauthenticate();
  }

  async verifyToken() {
    if (this.isAccessTokenExpired) {
      throw new Error('Verification Failed: Access Token Expired');
    }

    const url = this.config.TOKEN_VERIFY_URL;

    const accessToken = this.credentials?.[this.config.ACCESS_TOKEN_FIELD];

    if (!accessToken) {
      throw new Error('Verification Failed: Missing Credentials');
    }

    const payload = { token: accessToken };

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        'User-Timezone': getTimezone(),
      },
    });

    if (!response.ok) {
      throw new Error(`Verification Failed: ${response.status}`);
    }

    return this.credentials;

  }

  async refreshToken(loginAsToken) {
    if (this.isRefreshTokenExpired && !loginAsToken) {
      throw new Error('Refresh Failed: Refresh Token Expired');
    }

    const url = this.config.TOKEN_REFRESH_URL;
    const refreshToken = loginAsToken || this.credentials?.[this.config.REFRESH_TOKEN_FIELD];

    if (!refreshToken) {
      throw new Error('Refresh Failed: Missing Credentials');
    }

    const payload = { refresh: refreshToken };

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'User-Timezone': getTimezone(),
        },
      });

      if (!response.ok) {
        this.unauthenticate();
        throw new Error(`Refresh Failed: ${response.status}`);
      }

      const data = await response.json();
      return this.authenticate({ ...data, loginAs: !!loginAsToken });
    }
    catch (error) {
      this.unauthenticate();
      throw error;
    }

  }
}

export { JwtAuthProvider };
